/**
 * VideoPreviewPlayer - Plays generated video scenes synchronized with audio
 *
 * Features:
 * - Sequential scene playback synced with trimmed audio
 * - Timeline with scene markers
 * - Playhead scrubbing
 * - Individual scene preview
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Download } from 'lucide-react';

interface SceneResult {
  scene_id: number;
  status: string;
  video_url: string | null;
  cost_usd: number;
  start_time?: number;
  end_time?: number;
}

interface TrimSelection {
  start_time: number;
  end_time: number;
  duration: number;
}

interface VideoPreviewPlayerProps {
  sceneResults: SceneResult[];
  audioUrl: string;
  trimSelection?: TrimSelection;
  totalDuration: number;
  onTrimAdjust?: (trim: TrimSelection) => void;
}

export default function VideoPreviewPlayer({
  sceneResults,
  audioUrl,
  trimSelection,
  totalDuration,
  onTrimAdjust: _onTrimAdjust,
}: VideoPreviewPlayerProps) {
  // Note: _onTrimAdjust reserved for future trim adjustment feature
  // Refs
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [_isDragging, _setIsDragging] = useState(false);

  // Calculate scene timings based on trim selection
  const trimStart = trimSelection?.start_time || 0;
  const trimEnd = trimSelection?.end_time || totalDuration;
  const trimDuration = trimEnd - trimStart;

  // Get successful scenes with video URLs
  const successfulScenes = sceneResults.filter(s => s.status === 'complete' && s.video_url);

  // Calculate even distribution of scenes across trim duration
  const sceneDuration = successfulScenes.length > 0 ? trimDuration / successfulScenes.length : 0;
  const scenesWithTiming = successfulScenes.map((scene, index) => ({
    ...scene,
    localStart: index * sceneDuration,
    localEnd: (index + 1) * sceneDuration,
  }));

  // Get current scene based on playback time
  const getCurrentSceneIndex = useCallback((time: number) => {
    for (let i = 0; i < scenesWithTiming.length; i++) {
      if (time >= scenesWithTiming[i].localStart && time < scenesWithTiming[i].localEnd) {
        return i;
      }
    }
    return scenesWithTiming.length - 1;
  }, [scenesWithTiming]);

  // Sync audio time to video scene
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const audioTime = audio.currentTime;
      const localTime = audioTime - trimStart;
      setCurrentTime(Math.max(0, Math.min(localTime, trimDuration)));

      const newSceneIndex = getCurrentSceneIndex(localTime);
      if (newSceneIndex !== currentSceneIndex && newSceneIndex >= 0) {
        setCurrentSceneIndex(newSceneIndex);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setCurrentSceneIndex(0);
      if (audio) {
        audio.currentTime = trimStart;
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [trimStart, trimDuration, currentSceneIndex, getCurrentSceneIndex]);

  // Check if audio time exceeds trim end
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isPlaying) return;

    const checkBounds = () => {
      if (audio.currentTime >= trimEnd) {
        audio.pause();
        audio.currentTime = trimStart;
        setIsPlaying(false);
        setCurrentTime(0);
        setCurrentSceneIndex(0);
      }
    };

    const interval = setInterval(checkBounds, 100);
    return () => clearInterval(interval);
  }, [isPlaying, trimEnd, trimStart]);

  // Update video source when scene changes
  useEffect(() => {
    const video = videoRef.current;
    const scene = scenesWithTiming[currentSceneIndex];

    if (video && scene?.video_url) {
      // Calculate time within the scene
      const sceneLocalTime = currentTime - scene.localStart;
      const sceneProgress = Math.max(0, Math.min(sceneLocalTime / sceneDuration, 1));

      if (video.src !== scene.video_url) {
        setIsLoading(true);
        video.src = scene.video_url;
        video.load();
        video.onloadeddata = () => {
          setIsLoading(false);
          // Seek to corresponding position in video
          if (video.duration) {
            video.currentTime = sceneProgress * video.duration;
          }
          if (isPlaying) {
            video.play().catch(() => {});
          }
        };
      }
    }
  }, [currentSceneIndex, scenesWithTiming, sceneDuration, isPlaying]);

  // Play/Pause sync
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      // Start slightly before trim start to compensate for audio latency
      const AUDIO_LATENCY_COMPENSATION = 0.15; // 150ms buffer
      const targetTime = Math.max(0, trimStart - AUDIO_LATENCY_COMPENSATION);

      // Start at trim start if at beginning
      if (audio.currentTime < trimStart - 0.5 || audio.currentTime >= trimEnd) {
        audio.currentTime = targetTime;

        // Wait for the seek to complete before playing
        const handleSeeked = () => {
          audio.removeEventListener('seeked', handleSeeked);
          audio.play().catch(() => {});
          setIsPlaying(true);
        };
        audio.addEventListener('seeked', handleSeeked);
      } else {
        // Already at correct position, play immediately
        audio.play().catch(() => {});
        setIsPlaying(true);
      }
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.muted = !audio.muted;
      setIsMuted(!isMuted);
    }
  };

  const skipToScene = (index: number) => {
    const audio = audioRef.current;
    const scene = scenesWithTiming[index];
    if (audio && scene) {
      audio.currentTime = trimStart + scene.localStart;
      setCurrentSceneIndex(index);
      setCurrentTime(scene.localStart);
    }
  };

  const handleTimelineClick = (e: React.MouseEvent) => {
    if (!timelineRef.current || !audioRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * trimDuration;

    audioRef.current.currentTime = trimStart + newTime;
    setCurrentTime(newTime);
    setCurrentSceneIndex(getCurrentSceneIndex(newTime));
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentScene = scenesWithTiming[currentSceneIndex];

  return (
    <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
      {/* Video Display */}
      <div className="relative aspect-[9/16] max-h-[400px] bg-black flex items-center justify-center">
        {currentScene?.video_url ? (
          <video
            ref={videoRef}
            className="h-full w-auto max-w-full"
            muted // Video is muted, audio comes from audio element
            playsInline
            loop
          />
        ) : (
          <div className="text-zinc-500 text-sm">No video available</div>
        )}

        {isLoading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-jelly-500" />
          </div>
        )}

        {/* Scene indicator */}
        <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs">
          Scene {currentSceneIndex + 1} / {successfulScenes.length}
        </div>
      </div>

      {/* Timeline */}
      <div className="p-4">
        {/* Scene markers timeline */}
        <div
          ref={timelineRef}
          className="relative h-8 bg-zinc-900 rounded-lg cursor-pointer mb-3"
          onClick={handleTimelineClick}
        >
          {/* Scene segments */}
          {scenesWithTiming.map((scene, index) => {
            const left = (scene.localStart / trimDuration) * 100;
            const width = (sceneDuration / trimDuration) * 100;
            const isActive = index === currentSceneIndex;

            return (
              <div
                key={scene.scene_id}
                className={`absolute top-0 bottom-0 border-r border-zinc-700 transition-colors ${
                  isActive ? 'bg-jelly-500/40' : 'bg-zinc-800 hover:bg-zinc-700'
                }`}
                style={{ left: `${left}%`, width: `${width}%` }}
                onClick={(e) => {
                  e.stopPropagation();
                  skipToScene(index);
                }}
              >
                <span className="absolute inset-0 flex items-center justify-center text-[10px] text-zinc-400">
                  {index + 1}
                </span>
              </div>
            );
          })}

          {/* Playhead */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-jelly-500 z-10"
            style={{ left: `${(currentTime / trimDuration) * 100}%` }}
          >
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-jelly-500 rounded-full" />
          </div>
        </div>

        {/* Time display */}
        <div className="flex items-center justify-between text-xs text-zinc-400 mb-3">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(trimDuration)}</span>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => skipToScene(Math.max(0, currentSceneIndex - 1))}
            className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
            disabled={currentSceneIndex === 0}
          >
            <SkipBack className="w-5 h-5" />
          </button>

          <button
            onClick={togglePlay}
            className="p-3 bg-jelly-500 hover:bg-jelly-600 rounded-full transition-colors"
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-0.5" />
            )}
          </button>

          <button
            onClick={() => skipToScene(Math.min(successfulScenes.length - 1, currentSceneIndex + 1))}
            className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
            disabled={currentSceneIndex === successfulScenes.length - 1}
          >
            <SkipForward className="w-5 h-5" />
          </button>

          <button
            onClick={toggleMute}
            className="p-2 hover:bg-zinc-700 rounded-lg transition-colors ml-4"
          >
            {isMuted ? (
              <VolumeX className="w-5 h-5" />
            ) : (
              <Volume2 className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Scene thumbnails */}
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
          {scenesWithTiming.map((scene, index) => (
            <button
              key={scene.scene_id}
              onClick={() => skipToScene(index)}
              className={`flex-shrink-0 w-16 h-28 rounded-lg overflow-hidden border-2 transition-colors ${
                index === currentSceneIndex ? 'border-jelly-500' : 'border-transparent hover:border-zinc-600'
              }`}
            >
              {scene.video_url ? (
                <video
                  src={scene.video_url}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                />
              ) : (
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-500">
                  {scene.scene_id}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Download individual scenes */}
        <div className="mt-4 pt-4 border-t border-zinc-700">
          <h4 className="text-sm font-medium mb-2">Download Scenes</h4>
          <div className="flex flex-wrap gap-2">
            {scenesWithTiming.map((scene) => (
              scene.video_url && (
                <a
                  key={scene.scene_id}
                  href={scene.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-xs transition-colors"
                >
                  <Download className="w-3 h-3" />
                  Scene {scene.scene_id}
                </a>
              )
            ))}
          </div>
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} src={audioUrl} preload="auto" />
    </div>
  );
}
